import { Link } from 'react-router';
import { LineChart, ClipboardList, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SymptomBurden } from '@/components/progress/SymptomBurden';
import { ProgressHeadline } from '@/components/progress/ProgressHeadline';
import { MetricGroupCard } from '@/components/progress/MetricGroupCard';
import { Milestones } from '@/components/progress/Milestones';
import { useMyProgressReports } from '@/services/progress-reports';
import { METRIC_GROUPS, orderReports } from '@/lib/progress-trend';

/**
 * The client's own progress over time (#232).
 *
 * The client app had no history surface at all before this, so every state
 * here is deliberate: production currently holds zero progress reports, which
 * makes "no reports yet" the state almost every client will actually see.
 */
export default function Progress() {
    const { data, isLoading, isError, refetch } = useMyProgressReports();

    // Ordered here rather than trusted from the API. Reports may share a period
    // (#275) and both are kept — deduplicating would hide a real double entry.
    const reports = orderReports(data ?? []);

    return (
        // --brand-page (#edeceb) is the background the branding document draws
        // behind every screen. White cards on it are only 1.18:1 apart, so the
        // panels below carry a border as well as a fill.
        <div className="min-h-screen bg-brand-page py-6">
            <div className="mx-auto max-w-2xl px-4">
                <header className="mb-6">
                    <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
                        <LineChart className="h-6 w-6 text-brand-text-green" aria-hidden="true" />
                        My progress
                    </h1>
                    <p className="mt-1 text-gray-700">
                        How things have changed across the progress reports you have sent your
                        coach.
                    </p>
                </header>

                {isLoading ? (
                    <Panel>
                        <p className="text-gray-700" role="status">
                            Loading your progress…
                        </p>
                    </Panel>
                ) : isError ? (
                    <Panel>
                        <div role="alert">
                            <AlertCircle className="h-8 w-8 text-amber-700" aria-hidden="true" />
                            <h2 className="mt-3 text-lg font-bold text-gray-900">
                                We could not load your progress
                            </h2>
                            <p className="mt-1 text-gray-700">
                                This is usually a connection problem rather than anything wrong with
                                your reports.
                            </p>
                            <Button className="mt-4 min-h-11 px-6" onClick={() => refetch()}>
                                Try again
                            </Button>
                        </div>
                    </Panel>
                ) : reports.length === 0 ? (
                    <NoReportsYet />
                ) : (
                    <div className="space-y-4">
                        {/* The headline first, deliberately: a client opening
                            this page should read what changed before they read
                            a single reading. */}
                        <ProgressHeadline reports={reports} />

                        {reports.length === 1 && <OneReportNote />}

                        <Milestones reports={reports} />

                        {/* Body / Energy and sleep / Mood, rather than one flat
                            divided list of every metric. Each card answers one
                            question, which the flat list did not. */}
                        {METRIC_GROUPS.map((group) => (
                            <MetricGroupCard key={group.key} group={group} reports={reports}>
                                {/* The 0-76 burden score sits under Mood as
                                    supporting detail rather than leading, now
                                    that the two feeling counts above it say the
                                    same thing without inventing a scale. */}
                                {group.key === 'mood' && (
                                    <div className="mt-6 border-t border-gray-200 pt-5">
                                        <SymptomBurden reports={reports} />
                                    </div>
                                )}
                            </MetricGroupCard>
                        ))}

                        <Panel>
                            <p className="text-gray-700">
                                Your coach sees all of this alongside your notes. Anything here you
                                want to talk about is worth raising at your next session.
                            </p>
                            <div className="mt-4">
                                <Link to="/progress-report">
                                    <Button className="min-h-11 px-6">
                                        Fill in this week&rsquo;s report
                                    </Button>
                                </Link>
                            </div>
                        </Panel>
                    </div>
                )}
            </div>
        </div>
    );
}

function Panel({ children }: { children: React.ReactNode }) {
    return (
        <div className="rounded-2xl border border-brand-surface-edge bg-brand-surface p-5 shadow-sm sm:p-6">
            {children}
        </div>
    );
}

/**
 * The state almost every client is in today.
 *
 * It sends them to the form rather than showing an empty chart, because an
 * empty chart tells someone with no reports nothing they can act on.
 */
function NoReportsYet() {
    return (
        <Panel>
            <div className="py-4 text-center">
                <ClipboardList
                    className="mx-auto h-12 w-12 text-brand-text-green"
                    aria-hidden="true"
                />
                <h2 className="mt-4 text-xl font-bold text-gray-900">
                    Your progress starts with your first report
                </h2>
                <p className="mx-auto mt-2 max-w-md text-gray-700">
                    Once you have sent a weekly progress report, this page will show how your
                    weight, energy, sleep and symptoms are changing over time.
                </p>
                <div className="mt-6">
                    <Link to="/progress-report">
                        <Button className="min-h-11 px-6">Fill in my first report</Button>
                    </Link>
                </div>
            </div>
        </Panel>
    );
}

/** One report is a starting point, not a trend. Say so rather than draw a dot. */
function OneReportNote() {
    return (
        <Panel>
            <h2 className="text-lg font-bold text-gray-900">This is your starting point</h2>
            <p className="mt-1 text-gray-700">
                You have sent one report so far, so there is nothing to compare it against yet. Your
                trends will appear here after your next one.
            </p>
        </Panel>
    );
}
